/**
 * Upload Queue Store
 *
 * Module-level (app-global) mirror of the Uppy upload manager's per-file
 * state. Lives ABOVE the page layer: mounted once at the Inertia layout so
 * `router.visit` navigation never unmounts it and never interrupts an
 * in-flight transfer. The Uppy singleton (lib/uploads/manager.ts) is the
 * source of truth; it pushes slot snapshots in here for the corner queue
 * widget and any placeholder-slot consumer to render.
 *
 * Follows the sessionStore pattern: `subscribeWithSelector` + explicit
 * selectors + convenience hooks for minimal re-renders.
 */
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import type { UploadSlot } from "~/lib/uploads/types";
import { revokePreviewUrl } from "~/lib/uploads/previewFrame";

export interface UploadQueueState {
  // Ordered by insertion; keyed by Uppy file id.
  slots: Record<string, UploadSlot>;
  order: string[];
  // Collapsed vs expanded corner widget. Persists across navigation.
  collapsed: boolean;
  // True while Golden Retriever has restored ghost state that needs the user
  // to re-pick the file(s) to resume. Drives the widget's restored hint.
  awaitingReselect: boolean;
  // The corner card is dismissed — gone from the bottom-right, not merely
  // collapsed. Uploads keep running (the Uppy manager is independent of the
  // widget). Cleared — the card resurfaces — when a NEW upload is enqueued or
  // an in-flight upload fails while hidden; a silent failure while dismissed is
  // the same bug class as a silent 100% stall. Completion while dismissed does
  // NOT resurface (the user opted out; the ready email covers them).
  dismissed: boolean;
  // Server-side Upload ids hard-deleted this session. This is the signal that
  // clears a latched UploadVideoSlot: the latch deliberately outlives the
  // queue slot, so a real delete needs its own channel to tell the player its
  // video is gone.
  deletedUploadIds: ReadonlySet<number>;
}

export interface UploadQueueActions {
  upsertSlot: (slot: UploadSlot) => void;
  patchSlot: (id: string, patch: Partial<UploadSlot>) => void;
  removeSlot: (id: string) => void;
  clearFinished: () => void;
  setCollapsed: (collapsed: boolean) => void;
  setAwaitingReselect: (awaiting: boolean) => void;
  // Dismiss the corner card: hide it and purge terminal slots (completed /
  // cancelled), leaving in-flight slots to keep uploading invisibly.
  dismissQueue: () => void;
  // Record a server-side Upload as gone (X-delete, or a poll's 404). Any
  // latched player for it renders null from here on.
  markUploadDeleted: (uploadId: number) => void;
}

export type UploadQueueStore = UploadQueueState & UploadQueueActions;

const initialState: UploadQueueState = {
  slots: {},
  order: [],
  collapsed: false,
  awaitingReselect: false,
  dismissed: false,
  deletedUploadIds: new Set<number>(),
};

const TERMINAL_STATUSES: ReadonlySet<UploadSlot["status"]> = new Set([
  "processing",
  "cancelled",
  "error",
]);

export const useUploadQueueStore = create<UploadQueueStore>()(
  subscribeWithSelector((set) => ({
    ...initialState,

    upsertSlot: (slot) =>
      set((state) => {
        const isNew = !state.order.includes(slot.id);
        return {
          slots: { ...state.slots, [slot.id]: slot },
          order: isNew ? [...state.order, slot.id] : state.order,
          // A new upload resurfaces a dismissed card.
          dismissed: isNew ? false : state.dismissed,
        };
      }),

    patchSlot: (id, patch) =>
      set((state) => {
        const existing = state.slots[id];
        if (!existing) return state;
        // A slot entering `error` while the card is dismissed resurfaces it —
        // silent failure while hidden is the bug we refuse to ship.
        const enteredError = patch.status === "error" && existing.status !== "error";
        return {
          slots: { ...state.slots, [id]: { ...existing, ...patch } },
          dismissed: enteredError ? false : state.dismissed,
        };
      }),

    removeSlot: (id) =>
      set((state) => {
        revokePreviewUrl(state.slots[id]?.previewUrl ?? null);
        const { [id]: _removed, ...rest } = state.slots;
        return { slots: rest, order: state.order.filter((x) => x !== id) };
      }),

    clearFinished: () =>
      set((state) => {
        const keptOrder = state.order.filter((id) => {
          const s = state.slots[id];
          return s && s.status !== "processing" && s.status !== "cancelled" && s.status !== "error";
        });
        // Revoke preview object URLs for the slots being dropped.
        for (const id of state.order) {
          if (!keptOrder.includes(id)) revokePreviewUrl(state.slots[id]?.previewUrl ?? null);
        }
        const keptSlots: Record<string, UploadSlot> = {};
        for (const id of keptOrder) keptSlots[id] = state.slots[id];
        return { slots: keptSlots, order: keptOrder };
      }),

    setCollapsed: (collapsed) => set({ collapsed }),
    setAwaitingReselect: (awaitingReselect) => set({ awaitingReselect }),

    markUploadDeleted: (uploadId) =>
      set((state) => {
        if (state.deletedUploadIds.has(uploadId)) return state;
        const next = new Set(state.deletedUploadIds);
        next.add(uploadId);
        return { deletedUploadIds: next };
      }),

    dismissQueue: () =>
      set((state) => {
        // Purge terminal slots (completed / cancelled / a failure the user is
        // dismissing outright); keep in-flight slots so their transfers survive
        // the card going away.
        const keptOrder = state.order.filter(
          (id) => state.slots[id] && !TERMINAL_STATUSES.has(state.slots[id].status)
        );
        for (const id of state.order) {
          if (!keptOrder.includes(id)) revokePreviewUrl(state.slots[id]?.previewUrl ?? null);
        }
        const keptSlots: Record<string, UploadSlot> = {};
        for (const id of keptOrder) keptSlots[id] = state.slots[id];
        return { slots: keptSlots, order: keptOrder, dismissed: true };
      }),
  }))
);

// ============================================================================
// Selectors
// ============================================================================

export const selectOrderedSlots = (s: UploadQueueStore): UploadSlot[] =>
  s.order.map((id) => s.slots[id]).filter(Boolean);

// "Finishing…" counts: the bytes have streamed but `complete` is still in
// flight, so navigating away would abandon the completion round-trip.
export const selectHasActiveTransfer = (s: UploadQueueStore): boolean =>
  s.order.some((id) => {
    const status = s.slots[id]?.status;
    return status === "uploading" || status === "finishing";
  });

export const selectQueueCount = (s: UploadQueueStore): number => s.order.length;

// Every slot has reached SUCCESS (completed transfer, now `processing` server-
// side) and there's at least one slot. This — and ONLY this — arms auto-dismiss;
// any error, in-flight, or paused slot disqualifies it, so a failure never
// vanishes on its own.
export const selectAllSuccess = (s: UploadQueueStore): boolean =>
  s.order.length > 0 && s.order.every((id) => s.slots[id]?.status === "processing");

export interface QueueAggregate {
  activeCount: number; // uploading, finishing, or paused
  aggregatePercent: number; // bytes-weighted across active uploads
  anyProcessing: boolean; // a completed transfer whose video is still transcoding
}

// Header summary math. The aggregate percent is bytes-weighted (a 2GB file at
// 50% counts far more than a 5MB file at 50%), which is what a human reads as
// "how far along is the whole batch". `anyProcessing` drives the footer's
// close-the-tab line: a slot in "processing" whose Upload row is a video that
// has not finished renditions.
export function computeAggregate(slots: UploadSlot[]): QueueAggregate {
  const active = slots.filter(
    (s) => s.status === "uploading" || s.status === "finishing" || s.status === "paused"
  );
  const totalBytes = active.reduce((sum, s) => sum + s.size, 0);
  const doneBytes = active.reduce((sum, s) => sum + s.bytesUploaded, 0);
  const aggregatePercent =
    totalBytes > 0 ? Math.min(100, Math.round((doneBytes / totalBytes) * 100)) : 0;

  const anyProcessing = slots.some((s) => {
    if (s.status !== "processing") return false;
    const status = s.upload?.renditions_status;
    // Non-video (or already-complete) uploads need no "processing" footer.
    return status != null && status !== "completed" && status !== "failed";
  });

  return { activeCount: active.length, aggregatePercent, anyProcessing };
}

// ============================================================================
// Convenience hooks
// ============================================================================

export function useUploadSlots(): UploadSlot[] {
  // The selector derives a fresh array each call; useShallow keeps the
  // component from re-rendering (and looping) when the contents are equal.
  return useUploadQueueStore(useShallow(selectOrderedSlots));
}

export function useHasActiveTransfer(): boolean {
  return useUploadQueueStore(selectHasActiveTransfer);
}

export function useUploadQueueCollapsed(): boolean {
  return useUploadQueueStore((s) => s.collapsed);
}

export function useUploadQueueAwaitingReselect(): boolean {
  return useUploadQueueStore((s) => s.awaitingReselect);
}

export function useUploadQueueDismissed(): boolean {
  return useUploadQueueStore((s) => s.dismissed);
}

export function useUploadQueueAllSuccess(): boolean {
  return useUploadQueueStore(selectAllSuccess);
}

export function useUploadDeleted(uploadId: number | null): boolean {
  return useUploadQueueStore((s) =>
    uploadId != null ? s.deletedUploadIds.has(uploadId) : false
  );
}
